import { BimIndexClient } from "./bimIndexClient";
import type { OpenResult, Requests } from "./protocol";

/**
 * Process-wide BIM-index session: one worker, hosting the WASM module and at most
 * one loaded index. An engine holds open every file it reads and a lease on the
 * scene's index, so every consumer goes through this rather than starting a second
 * worker. {@link bimIndexBitmapIds} needs only the module, so a scene reading a
 * served index uses the worker with `bimIndexInfo()` `null` throughout.
 */

let client: BimIndexClient | null = null;
let info: OpenResult | null = null;
let opening: Promise<OpenResult> | null = null;
/** What the index open, or opening, was last asked to open, for {@link reopenBimIndex}. */
let openedWith: Requests["open"]["params"] | null = null;
/** Every `open` sent to the current worker and not answered yet, superseded or not. */
let opens = new Set<Promise<unknown>>();
/** The release {@link closeBimIndex} has in flight, which a second close waits on too. */
let releasing: Promise<void> | null = null;
const letGoListeners = new Set<(sceneDirName: string) => void>();

/** The session's worker client, started on first use. */
export function bimIndexClient(): BimIndexClient {
    if (!client) {
        const started: BimIndexClient = new BimIndexClient((event) => forgetLetGo(started, event.sceneDirName));
        client = started;
    }
    return client;
}

/**
 * Calls `listener` with the scene directory when the worker closes the open index because the
 * SDK is deleting the scene, possibly from another tab. The session has forgotten the index by
 * then, so the next open asks the worker again.
 * @returns The unsubscribe.
 */
export function onBimIndexLetGo(listener: (sceneDirName: string) => void): () => void {
    letGoListeners.add(listener);
    return () => void letGoListeners.delete(listener);
}

function forgetLetGo(from: BimIndexClient, sceneDirName: string) {
    // Ignore a worker that has been closed since, and an index of another scene opened since.
    if (client !== from || openedWith?.sceneDirName !== sceneDirName) {
        return;
    }
    info = null;
    opening = null;
    openedWith = null;
    for (const listener of letGoListeners) {
        listener(sceneDirName);
    }
}

export function bimIndexInfo(): OpenResult | null {
    return info;
}

/** Load the index, replacing whatever is currently open. */
export async function openBimIndex(params: Requests["open"]["params"]): Promise<OpenResult> {
    const opener = bimIndexClient();
    const pending = opener.call("open", params);
    openedWith = params;
    const inFlight = opens;
    inFlight.add(pending);
    opening = pending;
    try {
        const result = await pending;
        // A close or a newer open may have taken the session over meanwhile.
        if (client === opener && opening === pending) {
            info = result;
        }
        return result;
    } finally {
        inFlight.delete(pending);
        if (opening === pending) {
            opening = null;
        }
    }
}

/**
 * Open the scene's index again, if it is the one open or opening, on what the scene's last
 * completed sync stored. Until then the worker holds the folder it opened, so a sync can sweep
 * the folder it replaced only after this: from the sync's `committed` callback, that same sync;
 * later, the next. A reopen that fails leaves the open index answering.
 * @returns Once reopened, the lease on the folder it was open on given back — which is
 *   what a sweep right after needs — or once the reopen failed. Never rejects.
 */
export async function reopenBimIndex(sceneDirName: string): Promise<void> {
    const params = openedWith;
    if (!client || params?.sceneDirName !== sceneDirName || (!info && !opening)) {
        return;
    }
    await openBimIndex(params).catch((e: unknown) => {
        console.warn(`Could not reopen the BIM index of scene ${sceneDirName}; the one open stays:`, e);
    });
}

/**
 * Load the index unless it is already open (or opening) — the idempotent entry
 * point for callers that just need it available, such as the first query through
 * the data access layer.
 */
export function ensureBimIndexOpen(params: Requests["open"]["params"]): Promise<OpenResult> {
    if (info) {
        return Promise.resolve(info);
    }
    return opening ?? openBimIndex(params);
}

/**
 * Ids of the union of the serialized roaring bitmaps a served index answered with.
 * Starts the worker if need be, and opens nothing. Both legs are transfers, not copies:
 * **a `bitmaps` or `unionWith` array that owns its buffer is detached when this
 * returns** — hand over only what is no longer needed here.
 */
export function bimIndexBitmapIds(bitmaps: Uint8Array[], unionWith?: Uint32Array): Promise<Uint32Array> {
    const transfer = transferableBuffers(unionWith ? [...bitmaps, unionWith] : bitmaps);
    return bimIndexClient().call("bitmapIds", { bitmaps, unionWith }, transfer);
}

/** `ids` as portable roaring bytes. Copied rather than transferred: `ids` is the caller's. */
export function bimIndexSerializedIds(ids: Uint32Array): Promise<Uint8Array> {
    return bimIndexClient().call("serializeIds", { ids });
}

/**
 * Ids as portable roaring bytes — {@link bimIndexBitmapIds} reversed. Copied rather than
 * transferred: the input is usually a live `SelectionSet`'s `sortedIds`.
 */
export function bimIndexIdsBitmap(ids: Uint32Array): Promise<Uint8Array> {
    return bimIndexClient().call("idsBitmap", { ids });
}

/**
 * The distinct buffers of `views`, skipping any view that does not span its own.
 *
 * A transfer neuters the whole `ArrayBuffer` rather than the view, so a slice of a
 * shared one would detach its siblings, and the same buffer listed twice is a
 * `DataCloneError`. Those views are copied by the structured clone instead — which
 * costs a copy and is always correct.
 */
function transferableBuffers(views: ArrayBufferView[]): Transferable[] {
    const buffers = new Set<ArrayBufferLike>();
    for (const view of views) {
        if (view.byteOffset === 0 && view.byteLength === view.buffer.byteLength) {
            buffers.add(view.buffer);
        }
    }
    return [...buffers] as Transferable[];
}

/**
 * Forget the index and terminate the worker — the only way to hand its linear
 * memory back to the browser. The session is free of it at once; the worker is asked
 * to close first, so that this knows when the files and lease are free. Every `open`
 * in flight is waited out before that, as the worker doesn't serialize messages: a
 * `close` landing mid-open would release nothing. No timeout, as a worker mid-scan
 * answers only once the scan is done.
 * @returns Once the files and the lease are released, or the worker has died. A close
 *   called while another is in flight resolves with it. Never rejects.
 */
export function closeBimIndex(): Promise<void> {
    const closing = client;
    const pendingOpens = [...opens];
    client = null;
    info = null;
    opening = null;
    openedWith = null;
    opens = new Set();
    if (closing) {
        const previous = releasing;
        const release = (async () => {
            await Promise.allSettled(pendingOpens);
            // A worker that failed to close is terminated all the same.
            await closing.call("close", undefined).catch(() => {});
            closing.terminate();
            await previous;
        })();
        releasing = release;
        void release.then(() => {
            if (releasing === release) {
                releasing = null;
            }
        });
    }
    return releasing ?? Promise.resolve();
}
